"use client";

import { useElementWidth } from "@/hooks/useElementWidth";
import { IUserBase } from "@/modules/users/domain/IUser";
import { IBase } from "@umahealth/entities";
import { cn } from "@/utils/cn";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "@umahealth/occipital/client";
import { matchSorter } from "match-sorter";
import { useState } from "react";
/**
 * Interface for the BaseCombobox component props.
 */
export interface IBaseCombobox {
	defaultValue?: string
	className?: string
	/**
	 * List of bases to display in the combobox.
	 * Each base is represented by an object that conforms to the `IUserBase` interface.
	 */
	bases: (IUserBase | IBase)[];

	/**
	 * Controls whether the combobox's open state is managed via this variable.
	 * If not provided, the combobox defaults to opening when it gains focus.
	 */
	open?: boolean;

	/**
	 * Callback function that is called when the selected value changes.
	 * It receives the new value as a string.
	 */
	onValueChange?: (value: string) => void;

	/**
	 * The label for the input field.
	 * This is the text that will be displayed as the label of the combobox.
	 */
	inputLabel: string;

	/**
	 * Determines if the label is displayed outside the input field or integrated within it.
	 * If `true`, the label is shown outside; otherwise, it is integrated within the input.
	 * Default is `true`.
	 */
	labelOutside?: boolean;

	/**
	 * Error message to display below the combobox.
	 * If this is provided, the error message will be shown in red text.
	 */
	error?: string;
}

/**
 * A combobox component for selecting a base (location of the operator).
 * Controls whether the combobox's open state is managed via this variable.
 * If not provided, the combobox defaults to opening when it gains focus.
 */
export function BaseCombobox({
	bases,
	open,
	onValueChange,
	defaultValue,
	inputLabel,
	labelOutside = true,
	error,
	className
}: IBaseCombobox) {
	/** separo value de label para mostrar en el input el label */
	const [label, setLabel] = useState<string | null>(defaultValue??null);
	const [elementRef, width] = useElementWidth();

	return (
		<div className={cn("relative block", className)}>
			{labelOutside && <label htmlFor="buscarBase">{inputLabel}</label>}
			<Command
				ref={elementRef}
				className="rounded-lg group"
				filter={
					/**
					 * Custom filter function for matching the search input with the bases list.
					 *
					 * @param {string} value - The value of the CommandItem, typically a combination of operator's first and last name.
					 * @param {string} search - The search string input by the user.
					 * @returns {number} - Returns 1 if there is a match, otherwise 0.
					 */
					(value, search) => {
						const results = matchSorter(bases, search, {
							keys: ["id", "name", "trainTerminal", "CRPC"],
						});

						const match = results.some(
							(base) => String(base.id) === value
						);
						return match ? 1 : 0; // mostrar si hay coincidencia, no mostrar si no
					}
				}
			>
				<CommandInput
					id="buscarBase"
					data-cy="buscarBase"
					label={labelOutside === false ? inputLabel : undefined}
					value={label ?? undefined}
					setValue={setLabel}
					onValueChange={(newValue) => {
						setLabel(newValue);

						if (onValueChange) {
							onValueChange(newValue);
						}
					}}
					placeholder="Buscar por nombre"
					className="border-0 w-full"
				/>
				<div
					className={cn(
						label ? undefined : "sr-only",
						open
							? "not-sr-only focus-within:not-sr-only"
							: "focus-within:not-sr-only sr-only  group-focus-within:not-sr-only"
					)}
				>
					<CommandList
						className="absolute z-20 bg-white border border-grey-200 rounded-lg shadow-md"
						style={{
							width: `${width}px`,
						}}
					>
						<CommandEmpty>{`No encontramos resultados para "${label}"`}</CommandEmpty>
						{bases.map((base) => {
							return (
								<CommandItem
									key={base.id}
									value={String(base.id)}
									onClick={() => {
										if (onValueChange) {
											onValueChange(String(base.id));
										}
										setLabel(base.name);
									}}
								>
									<div className="flex flex-col text-left justify-start">
										{base.name}
										{(('CRPC' in base && base.CRPC) || ('trainTerminal' in base && base.trainTerminal)) && (
											<span className="text-xxs text-grey-600">
												CRPC: {('CRPC' in base ? base.CRPC : base.trainTerminal)}
											</span>
										)}
									</div>
								</CommandItem>
							);
						})}
					</CommandList>
				</div>
			</Command>
			{error && <p className="text-red-500">{error}</p>}
		</div>
	);
}
